/**
 * 石を「置くタイプ」 (Jungo / Renju / Connect6 / TwixT) 用の 2 タップ確定フック。
 *
 * - 1 回目のタップ: pending にセット (UI 上で半透明表示、 onMove は呼ばない)
 * - 同じ pending を 2 回目タップ: commit 実行 (= onMove を呼ぶ)
 * - 別の legal cell を 1 回目タップ: pending 切り替え
 * - 非 legal cell をタップ: pending クリア
 * - **その盤の局面が変わったら** 自動的に pending クリア
 *   (= 自分の commit 反映 / 相手手番 / リフレッシュ で先取の pending を残さない)
 *
 * 通信には乗らない (= サーバには 2 回目のタップ後にしか送らない)。 「指し直し」 と
 * 「誤タップ防止」 の両方の UX に効く。
 */

import { useEffect, useMemo, useState } from 'react';
import { contentKey } from '../lib/content-key';

export interface PendingConfirmApi<K extends string> {
  /** 現在 pending 中の key (= 「もう一度押すと commit」 状態の cell)。 */
  pending: K | null;
  /** タップ処理。 isLegal=false なら pending をクリア。 isLegal=true なら 1 回目セット / 2 回目 commit。 */
  tap: (key: K, isLegal: boolean, commit: () => void) => void;
  /** 外部から強制クリアしたいとき (= キャンセルボタン等)。 */
  clear: () => void;
}

export function usePendingConfirm<K extends string>(state: unknown): PendingConfirmApi<K> {
  const [pending, setPending] = useState<K | null>(null);
  // **参照ではなく中身**で比べる (lib/content-key.ts)。 参照で比べると、 並列マッチで
  // 相手が別の盤を指しただけで broadcast が来て、 この盤の局面が 1 手も進んでいないのに
  // 仮確定が消える。 ユーザーから見ると「2 タップ目の確定が効かない、 置き直すと効く」。
  // (2026-09-19: 3 盤 囲碁+連珠 の対人戦で、 相手が囲碁を打つと連珠の仮確定が消え、
  //  2 タップ目で石が増えないことを e2e で確認 → この比較に変更)
  const stateKey = useMemo(() => contentKey(state), [state]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: stateKey の変化そのものを effect の発火条件に使う意図。
  useEffect(() => {
    setPending(null);
  }, [stateKey]);
  return {
    pending,
    tap: (key, isLegal, commit) => {
      if (!isLegal) {
        setPending(null);
        return;
      }
      if (pending === key) {
        commit();
        setPending(null);
        return;
      }
      setPending(key);
    },
    clear: () => setPending(null),
  };
}
